import type { ReactNode } from "react";

/**
 * The placeholders that stand in for content on its way. They say WHERE things
 * will be, in the size they will be, so the page does not jump when the data
 * arrives — a bare spinner says nothing about the shape and, on a page that
 * returns it early, takes the title away with it.
 *
 * Decorative (`aria-hidden`): the region that holds them is `aria-busy` and
 * carries one "Loading…" for a screen reader — `LoadingRegion` for a group of
 * them, and `FormPlaceholder` / `Table` say it themselves.
 */

/**
 * The region that holds a group of placeholders: it says "Loading…" ONCE (a
 * polite status), whatever the number of bars and cards inside — four cards
 * that each announced themselves would be four announcements. `className` is the
 * LAYOUT of the group (a grid, a stack).
 */
export function LoadingRegion({
  label = "Loading…",
  className = "",
  children,
}: {
  label?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div role="status" aria-busy="true" className={className || undefined}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/**
 * One pulsing bar. `className` is the SIZE (`h-4 w-1/3`): the colour and the
 * shape are the bar's own. The pulse stands still for a reader who asked their
 * system for less motion (the global rule in `index.css`).
 */
export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse rounded-md bg-line${className ? ` ${className}` : ""}`}
    />
  );
}

/** A card's worth of bars, for a block whose shape is a title and two lines. */
export function CardSkeleton() {
  return (
    <div className="card space-y-3">
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}
